import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatBRL } from "../lib/api";
import { LineChart, TrendingUp, Wallet, Briefcase, Link2 } from "lucide-react";

function Card({ label, value, accent, testid }) {
  return (
    <div className="fc-card p-5 fc-card-hover" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3] font-medium">{label}</div>
      <div className={`fc-kpi-value text-2xl sm:text-[26px] mt-2 leading-none ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function ForecastPage() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/forecast").then(r => setD(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <div className="mb-7">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Projeção</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><LineChart size={26} className="text-[#D4AF37]"/> Previsão financeira</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Projeção de juros futuros considerando os ciclos ativos</p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[1,2,3,4].map(i=><div key={i} className="fc-card h-28 shimmer"/>)}</div>
      ) : d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
            <Card label="Capital na rua" value={formatBRL(d.carteira.capital_na_rua)} accent="text-[#D4AF37]" testid="kpi-capital"/>
            <Card label="Juros próximos 30 dias" value={formatBRL(d.carteira.juros_30)} accent="text-emerald-300" testid="kpi-30"/>
            <Card label="Juros próximos 60 dias" value={formatBRL(d.carteira.juros_60)} accent="text-emerald-300" testid="kpi-60"/>
            <Card label="Juros próximos 90 dias" value={formatBRL(d.carteira.juros_90)} accent="text-emerald-300" testid="kpi-90"/>
          </div>

          <section className="fc-card p-5 sm:p-6 mb-6" data-testid="forecast-by-socio">
            <h2 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2 mb-4"><Briefcase size={16} className="text-[#D4AF37]"/> Por sócio</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                    <th className="py-2 px-2">Sócio</th>
                    <th className="py-2 px-2">Capital</th>
                    <th className="py-2 px-2">30 dias</th>
                    <th className="py-2 px-2">60 dias</th>
                    <th className="py-2 px-2">90 dias</th>
                  </tr>
                </thead>
                <tbody>
                  {d.por_socio.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#737373]">Nenhum sócio vinculado a contratos ativos.</td></tr>}
                  {d.por_socio.map(s => (
                    <tr key={s.socio_id} className="border-b border-[#161616]">
                      <td className="py-2.5 px-2 font-medium">{s.name}</td>
                      <td className="py-2.5 px-2 font-mono-tab">{formatBRL(s.capital)}</td>
                      <td className="py-2.5 px-2 font-mono-tab text-emerald-300">{formatBRL(s.juros_30)}</td>
                      <td className="py-2.5 px-2 font-mono-tab text-emerald-300">{formatBRL(s.juros_60)}</td>
                      <td className="py-2.5 px-2 font-mono-tab text-emerald-300">{formatBRL(s.juros_90)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="fc-card p-5 sm:p-6" data-testid="forecast-by-indicador">
            <h2 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2 mb-4"><Link2 size={16} className="text-[#D4AF37]"/> Por indicador</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[#737373] border-b border-[#1f1f1f]">
                    <th className="py-2 px-2">Indicador</th>
                    <th className="py-2 px-2">Capital</th>
                    <th className="py-2 px-2">30 dias</th>
                    <th className="py-2 px-2">60 dias</th>
                    <th className="py-2 px-2">90 dias</th>
                  </tr>
                </thead>
                <tbody>
                  {d.por_indicador.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-[#737373]">Nenhum indicador.</td></tr>}
                  {d.por_indicador.map(i => (
                    <tr key={i.indicator_id} className="border-b border-[#161616]">
                      <td className="py-2.5 px-2 font-medium">{i.name}</td>
                      <td className="py-2.5 px-2 font-mono-tab">{formatBRL(i.capital)}</td>
                      <td className="py-2.5 px-2 font-mono-tab text-emerald-300">{formatBRL(i.juros_30)}</td>
                      <td className="py-2.5 px-2 font-mono-tab text-emerald-300">{formatBRL(i.juros_60)}</td>
                      <td className="py-2.5 px-2 font-mono-tab text-emerald-300">{formatBRL(i.juros_90)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </Layout>
  );
}
